import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, ScrollText, Loader2, FileText, Sparkles, ListChecks } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { cn } from '@/lib/utils'
import { classesApi, classesQueryKey, type ClassListItem } from '../api'
import { CreateClassDrawer } from '../components/CreateClassDrawer'
import { StatusBadge } from '../components/StatusBadge'
import { classMaturity } from '../lib/class-maturity'

function ExecStat({ value, label, tone = 'neutral' }: { value: number; label: string; tone?: 'neutral' | 'warning' }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={cn('data-mono text-base font-bold', tone === 'warning' ? 'text-warning' : 'text-fg-1')}>
        {value}
      </span>
      <span className="text-xs text-fg-3">{label}</span>
    </div>
  )
}

function MetaStat({ icon: Icon, value, label }: { icon: typeof Sparkles; value: number; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg-3">
      <Icon className="h-3.5 w-3.5 text-fg-3" />
      <span className="data-mono text-fg-2">{value}</span> {label}
    </span>
  )
}

function ClassCard({ c }: { c: ClassListItem }) {
  const maturity = classMaturity(c)
  return (
    <Card className="group transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md">
      <Link to={`/classes/${c.id}`} className="block px-5 py-4">
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <h3
            className="font-display font-bold text-[15px] leading-snug text-fg-1 line-clamp-1 group-hover:text-brand-700 transition-colors"
            title={c.name}
          >
            {c.name}
          </h3>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className="inline-flex items-center gap-1.5 text-2xs font-medium text-fg-3"
              title={maturity.description}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', maturity.dotClass)} />
              {maturity.label}
            </span>
            <StatusBadge status={c.status} />
          </div>
        </div>
        <p className="text-sm text-fg-3 line-clamp-2 min-h-[2.5em] mb-4">
          {c.definition || <span className="italic">Sem definição</span>}
        </p>
        <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-3">
          <div className="flex items-center gap-4">
            <MetaStat icon={Sparkles} value={c.signalCount} label="sinais" />
            <MetaStat icon={FileText} value={c.goldExampleCount} label="exemplos" />
            <MetaStat icon={ListChecks} value={c.ruleCount} label="regras" />
          </div>
          <span className="data-mono text-2xs text-fg-3/70 truncate max-w-[40%]" title={c.slug}>
            {c.slug}
          </span>
        </div>
      </Link>
    </Card>
  )
}

export function ClassesPage() {
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data: classes, isLoading } = useQuery({
    queryKey: classesQueryKey.list(),
    queryFn: classesApi.list,
  })

  const total = classes?.length ?? 0
  const withRules = classes?.filter((c) => c.ruleCount > 0).length ?? 0
  const incomplete = classes?.filter((c) => c.ruleCount === 0).length ?? 0

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <span className="data-mono text-2xs uppercase tracking-wider text-accent-600 mb-2 inline-block">
              Studio
            </span>
            <h1 className="font-display text-2xl font-bold text-fg-1">Classes</h1>
            <p className="text-sm text-fg-3 mt-1">
              Catálogo de classes de classificação. Crie, descreva, gere regex via LLM e promova pra produção.
            </p>
          </div>
          <Button onClick={() => setDrawerOpen(true)}>
            <Plus className="h-4 w-4" />
            Nova classe
          </Button>
        </div>

        {total > 0 && (
          <div className="flex items-center gap-6 border-t border-border-subtle pt-4">
            <ExecStat value={total} label={total === 1 ? 'classe' : 'classes'} />
            <span className="h-4 w-px bg-border-subtle" />
            <ExecStat value={withRules} label="com regras" />
            <span className="h-4 w-px bg-border-subtle" />
            <ExecStat value={incomplete} label="incompletas" tone={incomplete > 0 ? 'warning' : 'neutral'} />
          </div>
        )}
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-fg-3">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : !classes || classes.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="Nenhuma classe cadastrada ainda"
          description="Crie a primeira classe pra começar a treinar regras de classificação."
          action={
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" />
              Criar primeira classe
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
          {classes.map((c) => (
            <ClassCard key={c.id} c={c} />
          ))}
        </div>
      )}

      <CreateClassDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  )
}
